/**
 * Shared distance-based perspective scaling for marker (pin) rendering.
 *
 * Extracted from MarkerRendererManager so other marker visuals (e.g. the
 * placement highlight disc) can scale on exactly the same curve as the pins
 * and stay visually locked to them while zooming.
 */

/** Scale factor for very near markers. */
export const MAX_MARKER_FACTOR = 1;
/** Scale factor for very far markers. */
export const MIN_MARKER_FACTOR = 0.2;

export type ReferenceDistances = {
    near: number;
    far: number;
};

/**
 * Calculate reference distances based on scene bounding sphere.
 * This automatically adapts to the scene size.
 * @param boundingSphereRadius - Radius of the scene's bounding sphere
 * @returns Object with near and far reference distances
 */
export function calculateReferenceDistances(boundingSphereRadius: number | undefined): ReferenceDistances {
    if (!boundingSphereRadius || boundingSphereRadius === 0) {
        // Fallback for unknown scene size - conservative defaults that work better for small scenes
        return { near: 1, far: 20 };
    }

    // Scale reference distances proportionally to scene size
    // Near: 5% of scene radius (close-up perspective kicks in)
    // Far: 100% of scene radius (max distance for perspective effect)
    // This ensures the scaling works for tiny scenes (cm) to huge scenes (km)
    const near = Math.max(1, boundingSphereRadius * 0.05);
    const far = Math.max(near * 2, boundingSphereRadius);

    return { near, far };
}

/**
 * Perspective scale factor for a marker at the given camera distance.
 *
 * MAX_MARKER_FACTOR at or inside `near`, MIN_MARKER_FACTOR at or beyond
 * `far`, linear interpolation in between. This is the exact curve pins are
 * rendered with in MarkerRendererManager.
 */
export function perspectiveFactorForDistance(distance: number, { near, far }: ReferenceDistances): number {
    if (distance <= near) {
        // Very close markers get maximum scaling
        return MAX_MARKER_FACTOR;
    }
    if (distance >= far) {
        // Very far markers get minimum scaling
        return MIN_MARKER_FACTOR;
    }

    const distanceRange = far - near;
    if (distanceRange <= 0) {
        // Fallback to a safe value when range is zero or invalid to avoid division by zero
        return MIN_MARKER_FACTOR;
    }

    let normalizedDistance = (distance - near) / distanceRange;
    // Clamp to [0, 1] for robustness; for valid inputs this is a no-op
    if (normalizedDistance < 0) {
        normalizedDistance = 0;
    } else if (normalizedDistance > 1) {
        normalizedDistance = 1;
    }
    return MAX_MARKER_FACTOR - normalizedDistance * (MAX_MARKER_FACTOR - MIN_MARKER_FACTOR);
}
